import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useMallarFetch } from '@/hooks/useMallarFetch'

interface Props {
  open: boolean
  templateId: string | null
  sessionId: string | null
  onCreateContract: (templateId: string) => void
  onClose: () => void
}

type State = 'verifying' | 'success' | 'error'

const MAX_RETRIES = 4
const RETRY_DELAY_MS = 2000

export function PurchaseSuccessModal({
  open,
  templateId,
  sessionId,
  onCreateContract,
  onClose
}: Props) {
  const apiFetch = useMallarFetch()
  const [state, setState] = useState<State>('verifying')
  const [templateTitle, setTemplateTitle] = useState<string>('')
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null)
  const [invoicePdf, setInvoicePdf] = useState<string | null>(null)
  const attemptsRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const verify = async () => {
    if (!templateId || !sessionId) {
      setState('error')
      return
    }
    try {
      const res = await apiFetch(
        `/api/templates/${templateId}/verify-purchase?session_id=${encodeURIComponent(sessionId)}`
      )
      if (res.ok) {
        const data = await res.json()
        if (data.verified) {
          setTemplateTitle(data.template_title || templateId)
          setReceiptUrl(data.receipt_url || null)
          setInvoicePdf(data.invoice_pdf || null)
          setState('success')
          return
        }
      }
    } catch {
      // fall through to retry
    }

    attemptsRef.current += 1
    if (attemptsRef.current < MAX_RETRIES) {
      timerRef.current = setTimeout(verify, RETRY_DELAY_MS)
    } else {
      setState('error')
    }
  }

  useEffect(() => {
    if (!open) return
    setState('verifying')
    attemptsRef.current = 0
    verify()
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [open, templateId, sessionId])

  const handleCreate = () => {
    if (templateId) onCreateContract(templateId)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-md">
        {state === 'verifying' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                Bekräftar betalning...
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground py-4">
              Vänligen vänta medan vi bekräftar din betalning.
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                Betalning genomförd!
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-2">
              <p className="text-sm text-muted-foreground">
                Du har köpt <span className="font-medium text-foreground">{templateTitle}</span>.
              </p>
              {(receiptUrl || invoicePdf) ? (
                <a
                  href={receiptUrl || invoicePdf!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline-offset-4 hover:underline"
                >
                  Visa kvitto
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">
                  En faktura har skickats till din e-postadress.
                </p>
              )}
            </div>
            <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>
                Senare
              </Button>
              <Button onClick={handleCreate} className="flex-1 sm:flex-none">
                Skapa avtal
              </Button>
            </DialogFooter>
          </>
        )}

        {state === 'error' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-amber-500 shrink-0" />
                Betalningen verifieras
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground py-2">
              Din betalning behandlas — du får en bekräftelse via e-post när den är klar.
              Mallen visas som köpt när du laddar om sidan.
            </p>
            <DialogFooter className="pt-2">
              <Button onClick={onClose}>Stäng</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
